import React, { useState } from "react";
import "./Biostatistics.css";
import PageHeader from "../../../components/headers/PageHeader";
import wave from "../../../assets/Images/waves/wave_mou.png";
import Flag from "react-world-flags";

export default function Biostatistics() {
    const [caseOpenIdx, setCaseOpenIdx] = useState(0);

    const caseItems = [
        {
            title: "Case Study I",
            points: ["Situation:", "Task:", "Action:", "Result:"],
        },
        {
            title: "Case Study II",
            points: ["Situation:", "Task:", "Action:", "Result:"],
        },
        {
            title: "Case Study III",
            points: ["Situation:", "Task:", "Action:", "Result:"],
        },
        {
            title: "Case Study IV",
            points: ["Situation:", "Task:", "Action:", "Result:"],
        },
        {
            title: "Case Study V",
            points: ["Situation:", "Task:", "Action:", "Result:"],
        },
    ];

    const countries = [
        {
            code: "DE",
            title: "Germany (AMNOG/IQWiG)",
            text: "Preparation of Module 4 dossiers with focus on patient-relevant outcomes, time-to-event methods, and validated responder analyses. Skilled in anchored indirect comparisons.",
        },
        {
            code: "FR",
            title: "France (HAS)",
            text: "Alignment with SMR/ASMR evaluations, emphasizing clinically meaningful benefit through head-to-head data or validated indirect evidence.",
        },
        {
            code: "GB",
            title: "UK (NICE)",
            text: "Expertise in advanced survival modelling, treatment-switching adjustments, and extrapolation consistent with NICE DSU guidelines.",
        },
        {
            code: "EU",
            title: "EU (EMA/EU-HTA)",
            text: "Incorporation of ICH E9(R1) estimands, sensitivity analyses, and indirect/NMA evidence for joint clinical assessments.",
        },
        {
            code: "US",
            title: "USA (FDA)",
            text: "Mastery of multiplicity control, adaptive design simulations, and pre-specification of endpoints to ensure Type I error control and transparent reporting.",
        },
        {
            code: "JP",
            title: "Japan (PMDA)",
            text: "Integration of ICH E9(R1) estimand principles with sensitivity analyses and proactive consultation practices.",
        },
        {
            code: "AU",
            title: "Australia (PBAC)",
            text: "Specialization in survival extrapolation, surrogate validation, and transparent uncertainty management for reimbursement dossiers.",
        },
        {
            code: "CA",
            title: "Canada (CADTH)",
            text: "Structured submissions addressing non-inferiority margins, intercurrent event strategies, and real-world evidence integration.",
        },
    ];

    return (
        <div className="biostats-page">
            <PageHeader
                mediaSrc={wave}
                mediaType="image"
                subtitle="Enhance Clinical Insight"
                title="Clinical Biostatistics"
            />

            <section className="biostats-section biostats-introSection">
                <h2>Statistical Strategy Across the Clinical Lifecycle</h2>

                <p>
                    Effective clinical development begins with robust statistical
                    planning. At Eupraxia, we provide a seamless framework that spans the
                    full clinical lifecycle — from first-in-human Phase I studies in India
                    to multinational Phase IV post-marketing programs.
                </p>

                <p>
                    With unwavering ICH-GCP compliance, patient-first ethics, and adaptive
                    methodologies, we empower sponsors to move confidently from bench to
                    bedside. Our global reach extends across the USA, Europe, India,
                    Australia, and Africa — delivering harmonized solutions that optimize
                    timelines, elevate data integrity, and ensure regulatory submission
                    readiness.
                </p>
            </section>

            <section className="biostats-section biostats-capabilitiesSection">
                <h2>Biostatistical Capabilities</h2>

                <p className="biostats-sectionIntro">
                    Our biostatistical consultancy is embedded at every stage of
                    development, ensuring trials are designed, powered, and executed with
                    scientific precision. By combining statistical expertise with
                    therapeutic insight, we reduce uncertainty and support confident
                    regulatory engagement.
                </p>

                <div className="biostats-cardGrid">
                    <article className="biostats-card">
                        <h3>Trial Design Collaboration</h3>
                        <ul className="biostats-list">
                            <li>Define clinically meaningful, statistically defensible endpoints.</li>
                            <li>Estimate and validate sample sizes with simulations.</li>
                            <li>Develop robust randomization strategies.</li>
                            <li>Provide statistical inputs for protocols, briefing books, and FDA meetings.</li>
                            <li>Support design decisions within the estimand framework.</li>
                        </ul>
                    </article>

                    <article className="biostats-card">
                        <h3>Core Statistical Services</h3>
                        <ul className="biostats-list">
                            <li>Statistical Analysis Plan development, review, and execution.</li>
                            <li>Preparation of TLF mock shells and regulatory-ready outputs.</li>
                            <li>Interim and final analyses for safety and efficacy.</li>
                            <li>FDA briefing book review, input, and sponsor training.</li>
                            <li>Post-hoc analyses and regulatory responses.</li>
                            <li>Survival models, repeated measures, longitudinal modelling, and PK/PD evaluation.</li>
                            <li>Sample Size Re-estimations.</li>
                        </ul>
                    </article>

                    <article className="biostats-card">
                        <h3>Regulatory Deliverables</h3>
                        <ul className="biostats-list">
                            <li>CDISC-compliant SDTM and ADaM datasets.</li>
                            <li>Generation of TLFs aligned with ICH E3.</li>
                            <li>Integrated Summaries of Safety and Efficacy.</li>
                            <li>Statistical review of draft CSRs and dossier contributions.</li>
                            <li>Transparent and timely responses to regulatory queries.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="biostats-section biostats-methodSection">
                <h2>Special Biostatistical Methodologies</h2>

                <div className="biostats-methodGrid">
                    <article className="biostats-methodCard">
                        <h3>Interim Analysis</h3>
                        <p>
                            Interim analyses are planned evaluations of accumulating trial
                            data conducted before study completion. They are typically
                            performed to assess early efficacy, futility, or safety outcomes.
                            These analyses are most valuable in pivotal Phase II/III trials,
                            where early signals can guide continuation, modification, or
                            termination of the study.
                        </p>
                        <p>
                            Group sequential designs such as O’Brien-Fleming and Pocock
                            boundaries are commonly used to maintain integrity. While interim
                            analyses can shorten timelines and reduce patient exposure to
                            ineffective treatments, they require rigorous data cleaning and
                            strict protection against operational bias.
                        </p>
                    </article>

                    <article className="biostats-methodCard">
                        <h3>Sample Size Re-Estimation</h3>
                        <p>
                            Sample size re-estimation methods are used when initial
                            assumptions such as variance, event rate, or effect size are
                            uncertain. These approaches allow mid-trial recalibration of
                            sample size to preserve statistical power without compromising
                            Type I error.
                        </p>
                        <p>
                            SSR can be blinded or unblinded depending on the study design.
                            The major advantage is flexibility, ensuring trials are neither
                            underpowered nor unnecessarily large. However, SSR requires
                            strong simulation, regulatory justification, and careful
                            operational controls.
                        </p>
                    </article>
                </div>
            </section>

            <section className="biostats-section biostats-globalSection">
                <h2>Global Statistical Submission Expertise</h2>

                <p className="biostats-sectionIntro">
                    In addition to robust trial design and analysis, our biostatistical
                    consultancy brings <strong>deep knowledge of country-specific
                    regulatory and HTA requirements</strong>, ensuring submissions are
                    compliant, persuasive, and market-ready.
                </p>

                <div className="biostats-countryGrid">
                    {countries.map((country) => (
                        <article className="biostats-countryCard" key={country.code}>
                            <div className="biostats-countryHead">
                                <Flag
                                    code={country.code}
                                    className="biostats-countryFlag"
                                    title={country.title}
                                />
                                <h3>{country.title}</h3>
                            </div>

                            <p>{country.text}</p>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
}